import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  Icon,
  showToast,
  Checkbox,
  Textarea,
} from '@repo/ui';
import { reportingEmailQueueApi } from '../../api';
import { ArEmailQueueMatter, ArEmailRemove } from '../../types/emailQueue';
import { useArEmailCollectionsStore } from '@xtnd/web/shared/stores';
import { useQueryClient } from '@tanstack/react-query';
import { ArEmailGroupByOption } from '@repo/types';
interface IRemoveEmailModal {
  open: boolean;
  onClose: () => void;
  matter?: ArEmailQueueMatter | null;
  matters?: ArEmailQueueMatter[] | [];
  groupBy?: ArEmailGroupByOption;
  bulkRemove?: boolean;
  itemsToBeDelete?: { matterId: string | undefined; clientId: string | undefined }[];
}

export const RemoveEmailModal = React.memo(
  ({ open, onClose, matter, bulkRemove, itemsToBeDelete }: IRemoveEmailModal) => {
    const [formData, setFormData] = useState<ArEmailRemove | null>(null);
    const [isRemoving, setIsRemoving] = useState<boolean>(false);
    const { setSelectedKeys, updateMatters } = useArEmailCollectionsStore();
    useEffect(() => {
      setFormData({
        isInPreview: false,
        reason: '',
      });
    }, []);

    const queryClient = useQueryClient();

    const handleBulkRemoveEmail = async () => {
      if (!itemsToBeDelete?.length) return;
      if (!formData) return;
      const bulkBody = {
        isInPreview: formData?.isInPreview,
        reason: formData?.reason,
        items: itemsToBeDelete.map(ite => ({
          matterId: ite?.matterId,
          clientId: ite?.clientId,
        })),
      };
      const result = await reportingEmailQueueApi.removeEmailQueueBulk(bulkBody);
      return result;
    };

    const handleRemoveEmail = async () => {
      if (!formData) return;

      if (!formData.isInPreview && !formData.reason) {
        showToast.error(`Please enter reason or select in review option.`);
        return;
      }

      try {
        setIsRemoving(true);
        const removedMatters: ArEmailQueueMatter[] = [];
        if (bulkRemove) {
          if (!itemsToBeDelete?.length) return;
          const result = await handleBulkRemoveEmail();
          if (result?.failed?.length) {
            showToast.warning(
              `${result.failed.length} item(s) failed to remove. Others were processed successfully.`
            );
          } else {
            showToast.success('Remove from email queue successfully.');
          }
          (result?.success ?? []).forEach(s =>
            removedMatters.push({
              matterId: s.matterId,
              clientId: s.clientId,
            } as ArEmailQueueMatter)
          );
        } else {
          await reportingEmailQueueApi.removeEmailQueue(
            matter?.matterId ?? '',
            matter?.clientId ?? '',
            formData
          );
          showToast.success(`Remove from email queue successfully.`);
          if (matter?.matterId) {
            removedMatters.push(matter);
          }
        }
        setSelectedKeys([]);
        if (removedMatters.length > 0) {
          // Prune local store snapshot so deepest drilldown (attorney → client →
          // matters) reflects removal immediately. Query refetch alone won't
          // touch `matters` store — it's seeded by user click, not by query.
          updateMatters(removedMatters);
        }
        queryClient.invalidateQueries({ queryKey: ['email-queue'] });
        queryClient.invalidateQueries({ queryKey: ['email-queue-sub-items'] });
      } catch (error) {
        showToast.error(
          `Remove from email queue failed: ${error instanceof Error ? error?.message : 'Unknown error'}`
        );
      } finally {
        setFormData({
          isInPreview: false,
          reason: '',
        });
        onClose();
        setIsRemoving(false);
      }
    };

    return (
      <Dialog open={open} onClose={onClose} size="lg">
        <DialogHeader>
          <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
            Remove {matter?.matterDisplayNumber} from email queue
          </h2>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <Checkbox
              label="In Review"
              checked={formData?.isInPreview}
              onChange={e =>
                setFormData(prev => (prev ? { ...prev, isInPreview: e.target.checked } : null))
              }
            />
          </div>
          <div className="space-y-2">
            <Textarea
              label="Reason"
              placeholder="Enter reason"
              value={formData?.reason}
              onChange={e =>
                setFormData(prev => (prev ? { ...prev, reason: e.target.value } : null))
              }
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={false}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            leftIcon={<Icon name="FiTrash" />}
            onClick={handleRemoveEmail}
            loading={isRemoving}
          >
            {isRemoving ? 'Removing...' : 'Remove'}
          </Button>
        </DialogFooter>
      </Dialog>
    );
  }
);

RemoveEmailModal.displayName = 'RemoveEmailModal';
